import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { waLink } from "../lib/site";
import { pick } from "../lib/leadFields";
import { getConsent, newEventId, visitorInfo } from "../lib/consent";
import { trackLead, trafficSource } from "../lib/tracking";
import { Field } from "./EnquiryForm";
import QuoteLines from "./QuoteLines";
import { WhatsAppIcon } from "./Brand";

const FIELDS = pick({ name: "name", label: "Contact person", required: true }, "phone", "whatsapp", "email", "company",
  "business_type", { name: "city", label: "Delivery location", required: true }, "state",
  { name: "message", label: "Additional details", type: "textarea", full: true });
const FALLBACK = ["AdBlue IBC Tank (1000 L)"];
const isIbc = (p) => p?.category === "ibc" || /\bIBC\b/i.test(`${p?.name ?? ""} ${p?.pack_size ?? ""}`);
const WA = "Hello MHM SUPER ADBLUE, I would like a quotation for IBC tanks.";

export default function IbcQuoteForm() {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const [options, setOptions] = useState(FALLBACK);
  const [lines, setLines] = useState([{ product: "", tanks: "1", date: "" }]);
  const [values, setValues] = useState({});
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const wanted = params.get("ibc") ?? "";
    api.get("/products").then((r) => {
      const names = (Array.isArray(r.data) ? r.data : []).filter(isIbc).map((p) => p.name);
      const list = names.length ? names : FALLBACK;
      setOptions(list);
      setLines((ls) => ls.map((l, i) => (i === 0 && !l.product ? { ...l, product: list.includes(wanted) ? wanted : list[0] } : l)));
    }).catch(() => {});
  }, [params]);

  useEffect(() => {
    if (hash !== "#ibc-quote") return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("ibc-quote");
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el?.scrollIntoView({ behavior: "smooth" });
    }, 450);
    return () => clearTimeout(t);
  }, [hash]);

  const total = useMemo(() => lines.reduce((s, l) => s + (parseInt(l.tanks, 10) || 0), 0), [lines]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const eventId = newEventId();
    const details = { Form: "IBC bulk quote" };
    lines.forEach((l, i) => { details[`Line ${i + 1}`] = `${l.product} × ${l.tanks} tank(s), deliver by ${l.date}`; });
    const first = lines[0]?.product ?? "IBC";
    const body = {
      type: "bulk", details, consent: agree, visitor_id: visitorInfo()?.visitor_id ?? "", page: window.location.pathname,
      source: trafficSource().slice(0, 60), marketing_consent: !!getConsent()?.marketing, event_id: eventId,
      product: `IBC quote: ${first}${lines.length > 1 ? ` +${lines.length - 1} more` : ""}`.slice(0, 120),
      quantity: `${total} IBC tank${total === 1 ? "" : "s"}`,
    };
    FIELDS.forEach((f) => { body[f.name] = (values[f.name] ?? "").trim(); });
    try {
      await api.post("/enquiries", body);
      trackLead("bulk", eventId);
      setDone(true);
      toast.success("Quote request received. Our team will contact you shortly.");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="ibc-quote-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h3 className="mt-4 font-display text-3xl font-bold uppercase text-navy">Quote requested!</h3>
        <p className="mt-2 text-slate-600">We received your request for {total} IBC tank{total === 1 ? "" : "s"} and will send pricing soon.</p>
        {values.email?.trim() && <p data-testid="ibc-quote-success-email" className="mt-1 text-sm text-slate-500">A confirmation email has been sent to {values.email.trim()}.</p>}
        <button data-testid="ibc-quote-new" onClick={() => { setValues({}); setAgree(false); setLines([{ product: options[0], tanks: "1", date: "" }]); setDone(false); }} className="mt-6 rounded-full border border-navy/20 px-5 py-3 text-sm font-semibold text-navy">
          Request another quote
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="ibc-quote-form" className="grid gap-5 sm:grid-cols-2">
      <QuoteLines lines={lines} setLines={setLines} options={options} />
      <p data-testid="ibc-quote-total" className="sm:col-span-2 font-mono text-sm uppercase tracking-widest text-slate-600">Total: <span className="font-bold text-navy">{total} tank{total === 1 ? "" : "s"}</span></p>
      {FIELDS.map((f) => <Field key={f.name} f={f} value={values[f.name]} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} testPrefix="ibc-quote" />)}
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-slate-600">
        <input type="checkbox" required checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="ibc-quote-consent-checkbox" className="mt-0.5 h-4 w-4 accent-[#FF6B00]" />
        <span>I agree that MHM SUPER ADBLUE may contact me by phone, WhatsApp or email about this enquiry. See our <Link to="/privacy-policy" className="font-semibold text-brand">privacy policy</Link>.</span>
      </label>
      <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button type="submit" disabled={loading} data-testid="ibc-quote-submit-button" className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 font-semibold text-white transition-[background-color,transform] hover:bg-brand-dark hover:-translate-y-0.5 disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Request IBC quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </button>
        <a href={waLink(WA)} target="_blank" rel="noreferrer" data-testid="ibc-quote-whatsapp-button" className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 px-6 py-4 text-sm font-semibold text-navy transition-colors hover:border-[#25D366] hover:text-[#128C4B]">
          <WhatsAppIcon /> Or ask on WhatsApp
        </a>
      </div>
    </form>
  );
}
